/**
 * Admin Students Page
 *
 * Page for managing students (Director only).
 * Allows creating, editing, moving, and deactivating students.
 */

import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Spinner } from "../../components/ui";
import {
  useAdminClassGroups,
  useAdminLevels,
  useAdminStudents,
  useCreateStudent,
  useDeleteStudent,
  useHardDeleteStudent,
  useMoveStudentToClass,
  useReactivateStudent,
  useResetStudentPassword,
  useUpdateStudent,
} from "../../hooks/useAdmin";
import type {
  AdminStudent,
  CreateStudentRequest,
  UpdateStudentRequest,
} from "../../types/admin";
import { applyPhoneMask } from "../../utils";

const CLASS_TYPE_OPTIONS = [
  { value: "individual", label: "Individual" },
  { value: "duo", label: "Duo" },
  { value: "group", label: "Grupo" },
];

interface StudentFormData {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  password: string;
  classGroupId: string;
  levelCode: string;
  classType: "individual" | "duo" | "group";
}

const initialFormData: StudentFormData = {
  email: "",
  firstName: "",
  lastName: "",
  phone: "",
  password: "",
  classGroupId: "",
  levelCode: "A1",
  classType: "individual",
};

export function AdminStudentsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = searchParams.get("filter") || "";
  const levelFilter = searchParams.get("level") || "";
  const classFilter = searchParams.get("class") || "";
  const statusFilter = searchParams.get("status") || "active";
  const action = searchParams.get("action");

  const [selectedStudent, setSelectedStudent] = useState<AdminStudent | null>(
    null,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<StudentFormData>(initialFormData);
  const [actionModal, setActionModal] = useState<{
    type: "deactivate" | "reactivate" | "delete";
    student: AdminStudent;
  } | null>(null);
  const [moveModal, setMoveModal] = useState<AdminStudent | null>(null);
  const [moveToClassId, setMoveToClassId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [resetPasswordModal, setResetPasswordModal] = useState<{
    student: AdminStudent;
    newPassword?: string;
  } | null>(null);

  const { data: students, isLoading } = useAdminStudents({
    level: levelFilter || undefined,
    classGroup: classFilter || undefined,
    isActive:
      statusFilter === "inactive"
        ? false
        : statusFilter === "active"
          ? true
          : undefined,
  });
  const { data: levels } = useAdminLevels();
  const { data: classes } = useAdminClassGroups();

  const createStudent = useCreateStudent();
  const updateStudent = useUpdateStudent();
  const deleteStudent = useDeleteStudent();
  const reactivateStudent = useReactivateStudent();
  const hardDeleteStudent = useHardDeleteStudent();
  const moveStudent = useMoveStudentToClass();
  const resetPassword = useResetStudentPassword();

  // Filter students by search query and special filters
  const filteredStudents = students?.filter((student) => {
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (
        !student.fullName.toLowerCase().includes(query) &&
        !student.email.toLowerCase().includes(query)
      ) {
        return false;
      }
    }

    // Filter by no class
    if (filter === "no-class" && student.classGroup) {
      return false;
    }

    return true;
  });

  // Open create modal if action=create
  useEffect(() => {
    if (action === "create") {
      handleOpenCreate();
      setSearchParams((prev) => {
        prev.delete("action");
        return prev;
      });
    }
  }, [action]);

  const handleOpenCreate = () => {
    setFormData(initialFormData);
    setIsEditing(false);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (student: AdminStudent) => {
    setFormData({
      email: student.email,
      firstName: student.firstName,
      lastName: student.lastName,
      phone: student.phone || "",
      password: "",
      classGroupId: student.classGroup?.id || "",
      levelCode: student.levelCode || "A1",
      classType: student.classType || "individual",
    });
    setSelectedStudent(student);
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedStudent(null);
    setFormData(initialFormData);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isEditing && selectedStudent) {
      const data: UpdateStudentRequest = {
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone || undefined,
        classGroupId: formData.classGroupId || null,
        levelCode: formData.levelCode,
        classType: formData.classType,
      };

      await updateStudent.mutateAsync({ id: selectedStudent.id, data });
    } else {
      const data: CreateStudentRequest = {
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone || undefined,
        password: formData.password || undefined,
        classGroupId: formData.classGroupId || undefined,
        levelCode: formData.levelCode,
        classType: formData.classType,
      };

      await createStudent.mutateAsync(data);
    }

    handleCloseModal();
  };

  const handleDelete = async (
    type: "deactivate" | "reactivate" | "delete",
    studentId: string,
  ) => {
    if (type === "deactivate") {
      await deleteStudent.mutateAsync(studentId);
    } else if (type === "reactivate") {
      await reactivateStudent.mutateAsync(studentId);
    } else if (type === "delete") {
      await hardDeleteStudent.mutateAsync(studentId);
    }
    setActionModal(null);
  };

  const handleMove = async () => {
    if (!moveModal) return;

    await moveStudent.mutateAsync({
      id: moveModal.id,
      data: { classGroupId: moveToClassId || null },
    });

    setMoveModal(null);
    setMoveToClassId("");
  };

  const handleResetPassword = async () => {
    if (!resetPasswordModal) return;

    const result = await resetPassword.mutateAsync(resetPasswordModal.student.id);

    // Update modal to show the new password
    setResetPasswordModal({
      ...resetPasswordModal,
      newPassword: result.newPassword,
    });
  };

  const handleFilterChange = (key: string, value: string) => {
    setSearchParams((prev) => {
      if (value) {
        prev.set(key, value);
      } else {
        prev.delete(key);
      }
      // Reset other filters when changing main filter
      if (key === "filter" && value) {
        prev.delete("level");
        prev.delete("class");
      }
      return prev;
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-sand-50 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/admin"
            className="inline-flex items-center gap-1 text-earth-700 hover:text-earth-700 text-sm mb-2 transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Dashboard
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-display font-bold text-earth-800">
                Manage Students
              </h1>
              <p className="text-earth-700 text-sm mt-1">
                Create and manage students and their classes
              </p>
            </div>
            <button
              onClick={handleOpenCreate}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <i className="ri-user-add-line"></i>
              New Student
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-earth-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-earth-400"></i>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name or email..."
                  className="w-full pl-10 pr-4 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                />
              </div>
            </div>

            {/* Level Filter */}
            <select
              value={levelFilter}
              onChange={(e) => handleFilterChange("level", e.target.value)}
              className="px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
            >
              <option value="">All levels</option>
              {levels?.map((level) => (
                <option key={level.code} value={level.code}>
                  {level.code}
                </option>
              ))}
            </select>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => handleFilterChange("status", e.target.value)}
              className="px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="all">All</option>
            </select>

            {/* Special Filter */}
            <select
              value={filter}
              onChange={(e) => handleFilterChange("filter", e.target.value)}
              className="px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
            >
              <option value="">All</option>
              <option value="no-class">No class</option>
            </select>
          </div>
        </div>

        {/* Students Table */}
        <div className="bg-white rounded-xl border border-earth-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-earth-50 border-b border-earth-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-earth-600 uppercase tracking-wide">
                    Student
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-earth-600 uppercase tracking-wide">
                    Class
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-earth-600 uppercase tracking-wide hidden sm:table-cell">
                    Level
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-earth-600 uppercase tracking-wide hidden md:table-cell">
                    Type
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-earth-600 uppercase tracking-wide">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-earth-100">
                {filteredStudents?.map((student) => (
                  <tr
                    key={student.id}
                    className={`hover:bg-earth-50/50 ${!student.isActive ? "opacity-50" : ""}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-700 font-semibold text-sm">
                          {student.firstName.charAt(0)}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-earth-800 truncate">
                            {student.fullName}
                          </p>
                          <p className="text-xs text-earth-700 truncate">
                            {student.email}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {student.classGroup ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-olive-100 text-olive-700 rounded text-xs font-medium">
                          <i className="ri-group-line"></i>
                          {student.classGroup.name}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs font-medium">
                          <i className="ri-alert-line"></i>
                          No class
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      {student.levelCode ? (
                        <span className="px-2 py-1 bg-earth-100 text-earth-700 rounded text-xs font-medium">
                          {student.levelCode}
                        </span>
                      ) : (
                        <span className="text-earth-700 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className="text-sm text-earth-600 capitalize">
                        {CLASS_TYPE_OPTIONS.find(
                          (o) => o.value === student.classType,
                        )?.label || "-"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setMoveModal(student)}
                          className="p-1.5 hover:bg-olive-100 rounded-lg transition-colors"
                          title="Move to class"
                        >
                          <i className="ri-arrow-left-right-line text-olive-600"></i>
                        </button>
                        <button
                          onClick={() => setResetPasswordModal({ student, newPassword: undefined })}
                          className="p-1.5 hover:bg-blue-100 rounded-lg transition-colors"
                          title="Reset password"
                        >
                          <i className="ri-lock-password-line text-blue-600"></i>
                        </button>
                        <button
                          onClick={() => handleOpenEdit(student)}
                          className="p-1.5 hover:bg-earth-100 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <i className="ri-edit-line text-earth-500"></i>
                        </button>
                        {student.isActive ? (
                          <button
                            onClick={() =>
                              setActionModal({ type: "deactivate", student })
                            }
                            className="p-1.5 hover:bg-amber-50 rounded-lg transition-colors"
                            title="Deactivate"
                          >
                            <i className="ri-pause-circle-line text-amber-500"></i>
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={() =>
                                setActionModal({ type: "reactivate", student })
                              }
                              className="p-1.5 hover:bg-green-50 rounded-lg transition-colors"
                              title="Reactivate"
                            >
                              <i className="ri-play-circle-line text-green-500"></i>
                            </button>
                            <button
                              onClick={() =>
                                setActionModal({ type: "delete", student })
                              }
                              className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete permanently"
                            >
                              <i className="ri-delete-bin-line text-red-500"></i>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Empty State */}
          {filteredStudents?.length === 0 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-earth-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-user-3-line text-2xl text-earth-400"></i>
              </div>
              <p className="text-earth-600 font-medium">No students found</p>
              <p className="text-earth-700 text-sm mt-1">
                {searchQuery
                  ? "Try a different search"
                  : "Create a new student to get started"}
              </p>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="mt-4 text-sm text-earth-700">
          {filteredStudents?.length || 0} student
          {(filteredStudents?.length || 0) !== 1 ? "s" : ""} found
        </div>
      </div>

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={handleCloseModal}
          ></div>
          <div className="relative w-full max-w-lg bg-white rounded-xl shadow-xl overflow-hidden max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-earth-100 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-earth-800">
                {isEditing ? "Edit Student" : "New Student"}
              </h2>
              <button
                onClick={handleCloseModal}
                className="p-1.5 hover:bg-earth-100 rounded-lg transition-colors"
              >
                <i className="ri-close-line text-xl text-earth-500"></i>
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Name */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-earth-700 mb-1">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        firstName: e.target.value,
                      }))
                    }
                    placeholder="John"
                    className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-earth-700 mb-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        lastName: e.target.value,
                      }))
                    }
                    placeholder="Smith"
                    className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                    required
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-earth-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, email: e.target.value }))
                  }
                  placeholder="john@email.com"
                  className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                  required
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-earth-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, phone: applyPhoneMask(e.target.value) }))
                  }
                  maxLength={15}
                  placeholder="(81) 99999-9999"
                  className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                />
              </div>

              {/* Password (only for create) */}
              {!isEditing && (
                <div>
                  <label className="block text-sm font-medium text-earth-700 mb-1">
                    Password
                  </label>
                  <input
                    type="text"
                    value={formData.password}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        password: e.target.value,
                      }))
                    }
                    placeholder="Leave empty to use firstname123"
                    className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                  />
                  <p className="text-xs text-earth-700 mt-1">
                    Default password: first name in lowercase + 123
                  </p>
                </div>
              )}

              {/* Class Group */}
              <div>
                <label className="block text-sm font-medium text-earth-700 mb-1">
                  Class
                </label>
                <select
                  value={formData.classGroupId}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      classGroupId: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                >
                  <option value="">No class</option>
                  {classes?.map((classGroup) => {
                    const isFull =
                      classGroup.studentCount >= classGroup.maxStudents;
                    // Allow selecting if it's the current class (editing) or not full
                    const isCurrentClass =
                      isEditing &&
                      selectedStudent?.classGroup?.id === classGroup.id;
                    return (
                      <option
                        key={classGroup.id}
                        value={classGroup.id}
                        disabled={isFull && !isCurrentClass}
                      >
                        {classGroup.name} ({classGroup.levelCode} -{" "}
                        {classGroup.studentCount}/{classGroup.maxStudents})
                        {isFull && !isCurrentClass ? " - FULL" : ""}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Level & Type (only shown if no class) */}
              {!formData.classGroupId && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-earth-700 mb-1">
                      Level
                    </label>
                    <select
                      value={formData.levelCode}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          levelCode: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                    >
                      {levels?.map((level) => (
                        <option key={level.code} value={level.code}>
                          {level.code}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-earth-700 mb-1">
                      Type
                    </label>
                    <select
                      value={formData.classType}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          classType: e.target.value as
                            | "individual"
                            | "duo"
                            | "group",
                        }))
                      }
                      className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                    >
                      {CLASS_TYPE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-3 pt-4">
                <button
                  type="submit"
                  disabled={createStudent.isPending || updateStudent.isPending}
                  className="flex-1 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {createStudent.isPending || updateStudent.isPending ? (
                    <span className="flex items-center justify-center gap-2">
                      <Spinner size="sm" />
                      Saving...
                    </span>
                  ) : isEditing ? (
                    "Save Changes"
                  ) : (
                    "Create Student"
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2.5 text-earth-600 hover:text-earth-800 text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Move to Class Modal */}
      {moveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setMoveModal(null)}
          ></div>
          <div className="relative w-full max-w-md bg-white rounded-xl shadow-xl p-6">
            <h3 className="text-lg font-semibold text-earth-800 mb-4">
              Move {moveModal.firstName} to class
            </h3>

            <div className="mb-4">
              <p className="text-sm text-earth-700 mb-2">
                Current class:{" "}
                <span className="font-medium text-earth-700">
                  {moveModal.classGroup?.name || "No class"}
                </span>
              </p>
              <select
                value={moveToClassId}
                onChange={(e) => setMoveToClassId(e.target.value)}
                className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
              >
                <option value="">Remove from class</option>
                {classes?.map((classGroup) => {
                  const isFull =
                    classGroup.studentCount >= classGroup.maxStudents;
                  // Allow selecting current class (already counted) or classes that aren't full
                  const isCurrentClass =
                    moveModal?.classGroup?.id === classGroup.id;
                  return (
                    <option
                      key={classGroup.id}
                      value={classGroup.id}
                      disabled={isFull && !isCurrentClass}
                    >
                      {classGroup.name} ({classGroup.levelCode} -{" "}
                      {classGroup.studentCount}/{classGroup.maxStudents})
                      {isFull && !isCurrentClass ? " - FULL" : ""}
                      {isCurrentClass ? " (current)" : ""}
                    </option>
                  );
                })}
              </select>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handleMove}
                disabled={moveStudent.isPending}
                className="flex-1 px-4 py-2.5 bg-olive-600 hover:bg-olive-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                {moveStudent.isPending ? "Moving..." : "Move Student"}
              </button>
              <button
                onClick={() => setMoveModal(null)}
                className="flex-1 px-4 py-2.5 border border-earth-200 text-earth-600 hover:bg-earth-50 rounded-lg text-sm font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Confirmation Modal */}
      {actionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setActionModal(null)}
          ></div>
          <div className="relative w-full max-w-sm bg-white rounded-xl shadow-xl p-6">
            <div className="text-center">
              {/* Icon */}
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${
                  actionModal.type === "delete"
                    ? "bg-red-100"
                    : actionModal.type === "deactivate"
                      ? "bg-amber-100"
                      : "bg-green-100"
                }`}
              >
                <i
                  className={`text-xl ${
                    actionModal.type === "delete"
                      ? "ri-delete-bin-line text-red-600"
                      : actionModal.type === "deactivate"
                        ? "ri-pause-circle-line text-amber-600"
                        : "ri-play-circle-line text-green-600"
                  }`}
                ></i>
              </div>

              {/* Title */}
              <h3 className="text-lg font-semibold text-earth-800 mb-2">
                {actionModal.type === "delete"
                  ? "Delete Student Permanently?"
                  : actionModal.type === "deactivate"
                    ? "Deactivate Student?"
                    : "Reactivate Student?"}
              </h3>

              {/* Description */}
              <p className="text-sm text-earth-700 mb-2">
                <strong>{actionModal.student.fullName}</strong>
              </p>
              <p className="text-sm text-earth-700 mb-6">
                {actionModal.type === "delete"
                  ? "This action cannot be undone. The student and all associated data will be permanently removed."
                  : actionModal.type === "deactivate"
                    ? "The student will be deactivated and will no longer be able to access the portal."
                    : "The student will be reactivated and will regain access to the portal."}
              </p>

              {/* Actions */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() =>
                    handleDelete(actionModal.type, actionModal.student.id)
                  }
                  disabled={
                    deleteStudent.isPending ||
                    reactivateStudent.isPending ||
                    hardDeleteStudent.isPending
                  }
                  className={`flex-1 px-4 py-2.5 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
                    actionModal.type === "delete"
                      ? "bg-red-600 hover:bg-red-700"
                      : actionModal.type === "deactivate"
                        ? "bg-amber-600 hover:bg-amber-700"
                        : "bg-green-600 hover:bg-green-700"
                  }`}
                >
                  {deleteStudent.isPending ||
                  reactivateStudent.isPending ||
                  hardDeleteStudent.isPending
                    ? "Processing..."
                    : actionModal.type === "delete"
                      ? "Delete Permanently"
                      : actionModal.type === "deactivate"
                        ? "Deactivate"
                        : "Reactivate"}
                </button>
                <button
                  onClick={() => setActionModal(null)}
                  className="flex-1 px-4 py-2.5 border border-earth-200 text-earth-600 hover:bg-earth-50 rounded-lg text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setResetPasswordModal(null)}
          ></div>
          <div className="relative w-full max-w-md bg-white rounded-xl shadow-xl p-6">
            <div className="text-center">
              {/* Icon */}
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-lock-password-line text-xl text-blue-600"></i>
              </div>

              {/* Title */}
              <h3 className="text-lg font-semibold text-earth-800 mb-2">
                {resetPasswordModal.newPassword ? "Password Reset Successfully!" : "Reset Student Password?"}
              </h3>

              {/* Description */}
              <p className="text-sm text-earth-700 mb-2">
                <strong>{resetPasswordModal.student.fullName}</strong>
              </p>

              {!resetPasswordModal.newPassword ? (
                <>
                  <p className="text-sm text-earth-700 mb-2">
                    A senha será resetada para:
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
                    <code className="text-blue-700 font-mono font-semibold">
                      {resetPasswordModal.student.firstName.toLowerCase()}123
                    </code>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-earth-700 mb-2">
                    Nova senha do aluno:
                  </p>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-6">
                    <code className="text-green-700 font-mono font-semibold text-lg">
                      {resetPasswordModal.newPassword}
                    </code>
                  </div>
                  <p className="text-xs text-earth-700">
                    Certifique-se de copiar esta senha antes de fechar.
                  </p>
                </>
              )}

              {/* Actions */}
              <div className="flex items-center gap-3 mt-6">
                {!resetPasswordModal.newPassword ? (
                  <>
                    <button
                      onClick={handleResetPassword}
                      disabled={resetPassword.isPending}
                      className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                    >
                      {resetPassword.isPending ? "Resetting..." : "Reset Password"}
                    </button>
                    <button
                      onClick={() => setResetPasswordModal(null)}
                      className="flex-1 px-4 py-2.5 border border-earth-200 text-earth-600 hover:bg-earth-50 rounded-lg text-sm font-medium transition-colors"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setResetPasswordModal(null)}
                    className="flex-1 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    Close
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminStudentsPage;
